/** Typed client for the Nudge API. Dev requests go through the Vite proxy. */

const BASE = import.meta.env['VITE_API_BASE'] ?? '/api'
const TOKEN_KEY = 'nudge.token'

/** A category *slug*. Categories are user-owned rows, so this is an open string —
 * the five built-ins ('groceries', 'errands', …) are just the seeded ones. */
export type Category = string
export type Tier = 'free' | 'pro' | 'family'

/** The resolved category behind a slug. `color` and `icon` are palette/icon keys
 * (see CategoryIcon.tsx); both are tolerated as plain strings so an unknown value
 * from a newer server degrades to the fallback instead of throwing. */
export interface CategoryDef {
  id: string
  slug: string
  label: string
  color: string
  icon: string
  sort_order: number
}

export interface User {
  id: string
  email: string
  display_name: string
  tier: Tier
  default_radius_m: number
  /** Whether to *show* the admin console. Never the gate — every /admin route
   * re-checks server-side, and a non-admin gets a 404 from all of them. */
  is_admin: boolean
  is_super_admin: boolean
  /** Whether they have clicked the link in the confirmation email. Soft — it
   * gates the reminder banner, never access. */
  email_verified: boolean
}

/** The reply to anything that queues an email.
 *
 * `sent` is False when no relay is configured (dev, and any misconfigured
 * deploy) or when the relay refused the message. On endpoints the user is
 * actively waiting on — Resend — the server awaits the send before answering,
 * so `false` there means it genuinely did not go and `detail` says why. Show it.
 *
 * `retry_after_seconds` is the cooldown before another send is accepted; use it
 * to disable the button rather than letting the next tap earn a 429.
 */
export interface MailAccepted {
  sent: boolean
  detail: string
  retry_after_seconds: number | null
}

export interface Store {
  id: string
  osm_id: string
  name: string | null
  brand: string | null
  address: string | null
  opening_hours: string | null
  lat: number
  lon: number
  distance_m: number | null
  is_open: boolean | null
}

export interface ClosestMember {
  user_id: string
  display_name: string
  distance_m: number
  is_you: boolean
}

export interface Reminder {
  id: string
  title: string
  category: Category
  /** Embedded so a household member renders someone else's category correctly
   * without read access to their category list. Null once the slug stops resolving. */
  category_detail: CategoryDef | null
  merchant_class: string
  merchant_label: string
  radius_m: number
  active: boolean
  household_id: string | null
  created_by: string
  completed_at: string | null
  completed_by: string | null
  /** Null for reminders closed before completer tracking, and for deleted accounts. */
  completed_by_name: string | null
  snoozed_until: string | null
  created_at: string
  nearest_store: Store | null
  is_nearby: boolean
  closest_member: ClosestMember | null
}

export interface MerchantSuggestion {
  merchant_class: string
  label: string
  category: Category
  store_count: number | null
}

export interface Member {
  user_id: string
  display_name: string
  role: 'owner' | 'member'
}

export interface Household {
  id: string
  name: string
  invite_code: string
  owner_id: string
  members: Member[]
}

export interface TriggeredItem {
  reminder_id: string
  title: string
  category: Category
}

export interface Trigger {
  store_id: string
  store_name: string
  distance_m: number
  items: TriggeredItem[]
}

export interface PositionResult {
  triggered: Trigger[]
  nearby_reminder_ids: string[]
  evaluated: number
}

/** Carries the server's structured detail so callers can branch on it. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly detail: unknown,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** True when the free plan's 5-reminder cap blocked the write. */
  get isTierLimit(): boolean {
    return (
      this.status === 402 &&
      typeof this.detail === 'object' &&
      this.detail !== null &&
      (this.detail as { code?: string }).code === 'free_tier_limit'
    )
  }

  /** Seconds the server wants us to wait, from a 429 throttle. Null if absent. */
  get retryAfter(): number | null {
    if (typeof this.detail !== 'object' || this.detail === null) return null
    const v = (this.detail as { retry_after?: unknown }).retry_after
    return typeof v === 'number' && Number.isFinite(v) && v > 0 ? Math.ceil(v) : null
  }

  /** True when a new category's label slugified onto one the user already has. */
  get isDuplicateCategory(): boolean {
    return (
      this.status === 409 &&
      typeof this.detail === 'object' &&
      this.detail !== null &&
      (this.detail as { code?: string }).code === 'duplicate_category'
    )
  }
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

export function setToken(token: string | null): void {
  if (token) localStorage.setItem(TOKEN_KEY, token)
  else localStorage.removeItem(TOKEN_KEY)
}

function messageFrom(detail: unknown, status: number): string {
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail)) {
    // FastAPI validation errors.
    const first = detail[0] as { loc?: unknown[]; msg?: string } | undefined
    if (first?.msg) {
      const field = Array.isArray(first.loc) ? first.loc.at(-1) : undefined
      return field ? `${String(field)}: ${first.msg}` : first.msg
    }
  }
  if (detail && typeof detail === 'object') {
    const m = (detail as { message?: string }).message
    if (m) return m
  }
  return `Request failed (HTTP ${status})`
}

/** Exported for `adminApi.ts`, which needs the same auth/error handling but is
 * kept in its own module so the staff surface does not bloat the app client. */
export async function request<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const token = getToken()
  const headers = new Headers(init.headers)
  if (init.body) headers.set('Content-Type', 'application/json')
  if (token) headers.set('Authorization', `Bearer ${token}`)

  let res: Response
  try {
    res = await fetch(`${BASE}${path}`, { ...init, headers })
  } catch (cause) {
    throw new ApiError(
      0,
      null,
      `Cannot reach the Nudge API at ${BASE}. Is the backend running? ` +
        `(docker compose up -d) — ${String(cause)}`,
    )
  }

  if (res.status === 204) return undefined as T

  const text = await res.text()
  let body: unknown = null
  if (text) {
    try {
      body = JSON.parse(text)
    } catch {
      body = text
    }
  }

  if (!res.ok) {
    const detail =
      body && typeof body === 'object' && 'detail' in body
        ? (body as { detail: unknown }).detail
        : body
    if (res.status === 401) setToken(null)
    throw new ApiError(res.status, detail, messageFrom(detail, res.status))
  }

  return body as T
}

const qs = (params: Record<string, string | number | boolean | undefined>) => {
  const s = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined) s.set(k, String(v))
  }
  const out = s.toString()
  return out ? `?${out}` : ''
}

export const api = {
  register: (email: string, password: string, display_name: string) =>
    request<{ access_token: string }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, display_name }),
    }),

  login: (email: string, password: string) =>
    request<{ access_token: string }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  me: () => request<User>('/auth/me'),

  resendVerification: () =>
    request<MailAccepted>('/auth/verify/resend', { method: 'POST' }),

  /** Unauthenticated: the link is usually opened on a different device from the
   * one that signed up. */
  confirmEmail: (token: string) =>
    request<{ email_verified: boolean; display_name: string; already_verified: boolean }>(
      '/auth/verify/confirm',
      { method: 'POST', body: JSON.stringify({ token }) },
    ),

  forgotPassword: (email: string) =>
    request<MailAccepted>('/auth/password/forgot', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  /** Returns a session — completing a reset proves the mailbox, so there is no
   * reason to bounce them to sign-in afterwards. */
  resetPassword: (token: string, password: string) =>
    request<{ access_token: string }>('/auth/password/reset', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    }),

  updateMe: (patch: Partial<Pick<User, 'display_name' | 'tier' | 'default_radius_m'>>) =>
    request<User>('/auth/me', { method: 'PATCH', body: JSON.stringify(patch) }),

  categories: () => request<CategoryDef[]>('/categories'),

  createCategory: (body: { label: string; color: string; icon: string }) =>
    request<CategoryDef>('/categories', { method: 'POST', body: JSON.stringify(body) }),

  updateCategory: (
    id: string,
    patch: Partial<Pick<CategoryDef, 'label' | 'color' | 'icon' | 'sort_order'>>,
  ) => request<CategoryDef>(`/categories/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),

  deleteCategory: (id: string) => request<void>(`/categories/${id}`, { method: 'DELETE' }),

  listReminders: (lat?: number, lon?: number) =>
    request<Reminder[]>(`/reminders${qs({ lat, lon })}`),

  createReminder: (body: {
    title: string
    category: Category
    merchant_class: string
    merchant_label: string
    radius_m: number
    household_id?: string
  }) => request<Reminder>('/reminders', { method: 'POST', body: JSON.stringify(body) }),

  patchReminder: (id: string, patch: Record<string, unknown>) =>
    request<Reminder>(`/reminders/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  completeReminder: (id: string) =>
    request<Reminder>(`/reminders/${id}/complete`, { method: 'POST' }),

  /** Completed reminders, newest first. Carries no store/distance decoration. */
  listDone: (limit = 50, offset = 0) =>
    request<Reminder[]>(`/reminders/done${qs({ limit, offset })}`),

  reopenReminder: (id: string) =>
    request<Reminder>(`/reminders/${id}/reopen`, { method: 'POST' }),

  snoozeReminder: (id: string, minutes = 30) =>
    request<Reminder>(`/reminders/${id}/snooze${qs({ minutes })}`, { method: 'POST' }),

  deleteReminder: (id: string) =>
    request<void>(`/reminders/${id}`, { method: 'DELETE' }),

  findStores: (merchant_class: string, lat: number, lon: number, limit = 10) =>
    request<Store[]>(`/places/stores${qs({ merchant_class, lat, lon, limit })}`),

  // `title` is the reminder text the user already typed. The server treats it as
  // a hint that reorders the merchant classes — never as a filter — so passing
  // it can only improve the list, and omitting it restores the old behaviour.
  brands: (category: Category, lat: number, lon: number, title?: string) =>
    request<MerchantSuggestion[]>(`/places/brands${qs({ category, lat, lon, title })}`),

  suggestions: (category: Category, title?: string) =>
    request<MerchantSuggestion[]>(`/places/suggestions${qs({ category, title })}`),

  households: () => request<Household[]>('/households'),

  createHousehold: (name: string) =>
    request<Household>('/households', { method: 'POST', body: JSON.stringify({ name }) }),

  joinHousehold: (invite_code: string) =>
    request<Household>('/households/join', {
      method: 'POST',
      body: JSON.stringify({ invite_code }),
    }),

  inviteToHousehold: (householdId: string, email: string) =>
    request<MailAccepted>(`/households/${householdId}/invite`, {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  ping: (householdId: string, reminder_id: string, user_id: string) =>
    request<{ delivered: number; push_configured: boolean }>(
      `/households/${householdId}/ping`,
      { method: 'POST', body: JSON.stringify({ reminder_id, user_id }) },
    ),

  reportPosition: (lat: number, lon: number, accuracy_m: number | null) =>
    request<PositionResult>('/positions', {
      method: 'POST',
      body: JSON.stringify({ lat, lon, accuracy_m }),
    }),

  vapid: () => request<{ public_key: string; configured: boolean }>('/push/vapid'),

  subscribePush: (sub: { endpoint: string; p256dh: string; auth: string }) =>
    request<void>('/push/subscribe', { method: 'POST', body: JSON.stringify(sub) }),
}
